export interface ParsedRtsp {
  ip: string;
  port: number;
  path: string;
  username: string;
  password: string;
}

const DEFAULT_RTSP_PORT = 554;

/**
 * Parse an ONVIF-derived RTSP URI into the pieces camera onboarding needs.
 *
 * Handles: rtsp://[user[:pass]@]host[:port][/path[?query]]
 * Falls back to `fallbackIp` for the host and port 554 when absent. IPv4 only
 * (ONVIF discovery targets IPv4 subnets); a bracketed IPv6 host is returned
 * as-is in `ip` without port parsing.
 */
export const parseRtspUri = (
  uri: string | null | undefined,
  fallbackIp = ""
): ParsedRtsp => {
  const empty: ParsedRtsp = {
    ip: fallbackIp,
    port: DEFAULT_RTSP_PORT,
    path: "",
    username: "",
    password: "",
  };

  if (!uri || typeof uri !== "string") {
    return empty;
  }

  // Strip scheme (rtsp:// or rtsps://); tolerate a missing scheme.
  const withoutScheme = uri.replace(/^rtsps?:\/\//i, "");

  // Split authority from path at the first "/".
  const slashIndex = withoutScheme.indexOf("/");
  const authority =
    slashIndex === -1 ? withoutScheme : withoutScheme.slice(0, slashIndex);
  const path = slashIndex === -1 ? "" : withoutScheme.slice(slashIndex); // keeps leading "/"

  // Split optional credentials from host:port at the last "@".
  let credsPart = "";
  let hostPart = authority;
  const atIndex = authority.lastIndexOf("@");
  if (atIndex !== -1) {
    credsPart = authority.slice(0, atIndex);
    hostPart = authority.slice(atIndex + 1);
  }

  let username = "";
  let password = "";
  if (credsPart) {
    const colon = credsPart.indexOf(":");
    if (colon === -1) {
      username = decodeURIComponent(credsPart);
    } else {
      username = decodeURIComponent(credsPart.slice(0, colon));
      password = decodeURIComponent(credsPart.slice(colon + 1));
    }
  }

  // IPv6 literal (e.g. [::1]:554) — return the bracketed host, skip port parse.
  if (hostPart.startsWith("[")) {
    return {
      ip: hostPart || fallbackIp,
      port: DEFAULT_RTSP_PORT,
      path,
      username,
      password,
    };
  }

  let ip = hostPart;
  let port = DEFAULT_RTSP_PORT;
  const portColon = hostPart.lastIndexOf(":");
  if (portColon !== -1) {
    ip = hostPart.slice(0, portColon);
    const parsedPort = Number(hostPart.slice(portColon + 1));
    if (Number.isFinite(parsedPort) && parsedPort > 0) {
      port = parsedPort;
    }
  }

  return {
    ip: ip || fallbackIp,
    port,
    path,
    username,
    password,
  };
};
